import type {
  CreatableModel,
  DeletableModel,
  ModifiableModel,
} from 'shared/domain/baseModelTypes';
import type {
  Deletable,
  Identificable,
} from 'shared/types/commonView';
import type { EventDescriptionType } from 'shared/types/eventDescription';
import type { MapCoordinates } from 'shared/types/mapCoordinates';
import type { UserAccess } from 'shared/domain/issue/mapping/accessLevel/toOnView/types';
import type { UserWithPersonalData } from '../user/types/model';
import type { UserOnView } from '../user/types/view';
import type { LinkedDocumentationItem } from './linkedDocumentation';
import type { IssueWorkflowReference } from './stageReferences';
import type { WorkflowDecisionOutDto } from 'shared/domain/workflow/types';
import type { LastDecisionStatus } from 'shared/domain/workflow/lastDecisionStatus';

export interface EventDescriptionPartModel {
  type: EventDescriptionType;
}

export interface TextDescriptionModel extends EventDescriptionPartModel {
  type: EventDescriptionType.text;
  text: string;
}

export interface MentionModel extends EventDescriptionPartModel {
  type: EventDescriptionType.mention;
  user: string;
  issueId: string;
  userAccesses: UserAccess[];
}

export interface MentionConstructorModel
  extends EventDescriptionPartModel {
  type: EventDescriptionType.mention;
  user: UserOnView;
  issueId: string;
  siteId: string;
  processId: string;
  userAccesses: UserAccess[];
}

/**
 * Compact summary of the PDF-eligible attachments of an issue or a message. Carried
 * instead of the full `documents` arrays so the filtered-issues payload stays light;
 * used for the report availability and page estimate without every document's metadata.
 */
export type AttachmentCounts = {
  images: number;
  nonImages: number;
};

export type IssueEventModel = {
  title: string;
  type: string;
  description: EventDescriptionPartModel[];
  deleted: boolean;
  attachmentCounts?: AttachmentCounts;
} & Identificable &
  CreatableModel &
  ModifiableModel;

export type IncomingEmailMessageModel = {
  subject: string;
  incomingEmailMessageContent: string;
  deleted: boolean;
  attachmentCounts?: AttachmentCounts;
} & Identificable &
  CreatableModel &
  ModifiableModel;

interface EstimatedCost {
  cost: string;
  coveredBy: string | undefined;
}

interface FinalCost {
  outstanding: string;
  settled: string;
  coveredBy: string | undefined;
}

export enum IssueLocationType {
  pin = 'pin',
  area = 'area',
}

interface Point { x: number; y: number }
type Area = Point[];

export type IssueModel = {
  _id: string;
  primaryData: {
    site: string;
    title: string;
    assignee: UserWithPersonalData | undefined;
    description: string;
    detectionDate: string | undefined;
    subcontractors: string[];
    contractNumbers: string[];
    level: string;
    positionOnMap: MapCoordinates;
    executor: UserWithPersonalData | undefined;
    selectedLocationType: IssueLocationType;
    targetAreas: Area[];
    finalAreas: Area[];
  };
  extendedData: {
    workTypes: string[] | undefined;
    rootCauses: string[] | undefined;
    estimatedCost: EstimatedCost[] | undefined;
    finalCost: FinalCost[] | undefined;
    targetCompletionDate: string | undefined;
    finalCompletionDate: string | undefined;
    targetResponseToAuthorDate: string | undefined;
    finalResponseToAuthorDate: string | undefined;
    responseToAuthorDelay?: string;
    targetStartDate: string | undefined;
    finalStartDate: string | undefined;
    solutionProposal: string | undefined;
    impact: string | null | undefined;
    environmentalAspect: string | null | undefined;
    effect: string | null | undefined;
    hazardCategory: string[] | null | undefined;
    expectedFine: number | undefined;
    imposedFine: number | undefined;
    solutionMethod: string | undefined;
    subcontractorRepresentative: string | undefined;
    decisionToImposeFine: string | undefined;
    circumstances: string | undefined;
    issueVerification: string | undefined;
    delayReason: string | undefined;
    rejectionReason: string | undefined;
    proposedCorrectiveAction: string | undefined;
    costCode: string | undefined;
    daysOfInabilityToWork: number | undefined;
    personUnableToWork: string | undefined;
    spilledSubstance: string | undefined;
    waterLeakageScale: number | undefined;
    soilLeakageScale: number | undefined;
    contaminatedSoilScale: number | undefined;
    executedByCompanies: string[] | undefined;
    targetAmount: number | undefined;
    completedAmount: number | undefined;
    amountPercentage?: number;
    completionDateDelay?: string;
    startDateDelay?: string;
    numberOfEmployees: number | undefined;
    priority: string | null | undefined;
    projectStage: string | null | undefined;
    sickLeaveStartDate: string | undefined;
    sickLeaveEndDate: string | undefined;
    supervisor: string | undefined;
    victim: string | undefined;
    victimJobTitle: string | undefined;
    employeeNumber: string | undefined;
    employmentType: string | undefined;
    immediateMeasures: string | undefined;
    victimActivity: string | undefined;
    injuryTypes: string[] | undefined;
    injuredBodyParts: string[] | undefined;
    applicationNumber: string | undefined;
    productType: string | undefined;
    producerDistributor: string | undefined;
    typeModel: string | undefined;
    installationLocation: string | undefined;
    requirements: string[] | undefined;
    installationBasis: string[] | undefined;
    constructionProductDocuments: string[] | undefined;
    deviceDocuments: string[] | undefined;
    additionalDocuments: string[] | undefined;
  };
  inspection: string | undefined;
  protocolItem: string | undefined;
  process: string;
  userAccesses: UserAccess[];
  hashtag: string;
  inspectionNumberSearch?: string;
  inspectionNumberSearchNormalized?: string;
  events: IssueEventModel[];
  workflowDecisions: WorkflowDecisionOutDto[];
  /**
   * Only populated when the issue was loaded through `getFiltered()` (the
   * issues grid); other load paths leave this `undefined`. `null` is a
   * distinct, meaningful value ("N/A": workflow has no approval stages) and
   * must not be assumed when the field is simply not computed.
   */
  lastDecisionStatus?: LastDecisionStatus | null;
  /** Same caveat as `lastDecisionStatus` - only populated via `getFiltered()`. */
  latestDecisionId?: string | null;
  incomingEmailMessages: IncomingEmailMessageModel[];
  mainImage: string | undefined;
  emailAddress: string;
  linkedDocumentation?: LinkedDocumentationItem[];
} & CreatableModel &
  ModifiableModel &
  DeletableModel &
  IssueWorkflowReference;

export type CreateIssueModel = {
  process: string;
  projectId: string;
  primaryData: Pick<IssueModel['primaryData'], 'site'> &
    Partial<Omit<IssueModel['primaryData'], 'site'>>;
} & Omit<EditIssueModel, '_id' | 'process' | 'primaryData' | 'stageId'>;

export type CopyIssueModel = {
  process: string;
  projectId: string;
  primaryData: Pick<IssueModel['primaryData'], 'site'> &
    Omit<IssueModel['primaryData'], 'site'>;
} & Omit<EditIssueModel, '_id' | 'process' | 'primaryData' | 'stageId'>;

export type EditIssueModel = {
  _id: string;
  /** ObjectId pointing to the target stage in the workflow */
  stageId?: string;
} & Partial<Omit<IssueModel, '_id' | 'primaryData' | 'mainImage'>> & {
    primaryData?: Partial<IssueModel['primaryData']>;
  } & Partial<CreatableModel> &
  Partial<ModifiableModel> &
  Partial<Deletable> &
  Partial<{ mainImage: string | null }>;
